import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { BodyM, BodyS } from '#src/atoms/typography/index.tsx';
import type { Props as UseAmountWithConversionProps } from '#src/hooks/useAmountWithConversion.ts';
import { useAmountWithConversion } from '#src/hooks/useAmountWithConversion.ts';
import type { Color } from '#src/style/types.ts';

import type { AmountWithConversion_monetaryAmount } from './__generated__/index.graphql.ts';

const Line = styled.div<{ $column: boolean }>`
  display: flex;
  ${props =>
    props.$column
      ? `flex-direction: column;`
      : `align-items: baseline;
      gap: var(--half-unit);`};
`;

export type Props = UseAmountWithConversionProps & {
  column?: boolean;
  withApproxSymbol?: boolean;
  hideExponent?: boolean;
  actualMain?: string | null;
  actualExponent?: string | null;
  color?: Color;
  exponentColor?: Color;
};

export const AmountWithConversion = (props: Props) => {
  const {
    column = false,
    actualMain,
    withApproxSymbol = false,
    hideExponent,
    actualExponent,
    color = 'var(--c-onBase-primary)',
    exponentColor = 'var(--c-nd-600)',
    ...useAmountWithConversionProps
  } = props;
  const { main, exponent } = useAmountWithConversion(
    useAmountWithConversionProps
  );

  return (
    <Line $column={column}>
      {!!(actualMain || main) && (
        <BodyM bold as="p" color={color}>
          {actualMain || main}
        </BodyM>
      )}
      {!hideExponent && !!(actualExponent || exponent) && (
        <BodyS as="span" color={exponentColor}>
          {!!withApproxSymbol && '≈ '}
          {actualExponent || exponent}
        </BodyS>
      )}
    </Line>
  );
};

AmountWithConversion.fragments = {
  monetaryAmount: gql`
    fragment AmountWithConversion_monetaryAmount on MonetaryAmount {
      ...useAmountWithConversion_monetaryAmount
    }
    ${useAmountWithConversion.fragments.monetaryAmount}
  ` as TypedDocumentNode<AmountWithConversion_monetaryAmount>,
};
